import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AdminSettings() {
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setS(data); }, [data]);

  if (!s) return <p className="text-muted-foreground">Loading...</p>;

  const setPath = (path, value) => {
    setS((prev) => {
      const next = structuredClone(prev);
      let o = next;
      for (let i = 0; i < path.length - 1; i++) { o[path[i]] = o[path[i]] || {}; o = o[path[i]]; }
      o[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const F = ({ label, value, onChange, textarea, rows }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      {textarea ? <Textarea rows={rows || 6} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />
        : <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />}
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Settings</h1><p className="text-sm text-muted-foreground">Manage branding, content, SEO, legal and integrations.</p></div>
        <Button className="rounded-full" onClick={save} disabled={busy} data-testid="settings-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes</Button>
      </div>

      <Tabs defaultValue="brand" className="mt-6">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="brand">Branding</TabsTrigger>
          <TabsTrigger value="hero">Homepage</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="legal">Legal</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="mt-4 grid max-w-lg gap-4 bs-card p-6">
          <F label="Brand name" value={s.brand?.name} onChange={(v) => setPath(["brand", "name"], v)} />
          <F label="Tagline" value={s.brand?.tagline} onChange={(v) => setPath(["brand", "tagline"], v)} />
          <F label="Domain" value={s.brand?.domain} onChange={(v) => setPath(["brand", "domain"], v)} />
        </TabsContent>

        <TabsContent value="hero" className="mt-4 grid max-w-2xl gap-4 bs-card p-6" data-testid="settings-hero">
          <F label="Eyebrow" value={s.hero?.eyebrow} onChange={(v) => setPath(["hero", "eyebrow"], v)} />
          <F label="Title" value={s.hero?.title} onChange={(v) => setPath(["hero", "title"], v)} />
          <F label="Subtitle" value={s.hero?.subtitle} onChange={(v) => setPath(["hero", "subtitle"], v)} textarea rows={3} />
        </TabsContent>

        <TabsContent value="contact" className="mt-4 grid max-w-lg gap-4 bs-card p-6">
          <F label="Email" value={s.contact?.email} onChange={(v) => setPath(["contact", "email"], v)} />
          <F label="Phone" value={s.contact?.phone} onChange={(v) => setPath(["contact", "phone"], v)} />
          <F label="Address" value={s.contact?.address} onChange={(v) => setPath(["contact", "address"], v)} />
          <F label="Hours" value={s.contact?.hours} onChange={(v) => setPath(["contact", "hours"], v)} />
        </TabsContent>

        <TabsContent value="seo" className="mt-4 grid max-w-2xl gap-4 bs-card p-6">
          <F label="Meta title" value={s.seo?.title} onChange={(v) => setPath(["seo", "title"], v)} />
          <F label="Meta description" value={s.seo?.description} onChange={(v) => setPath(["seo", "description"], v)} textarea rows={3} />
          <F label="Keywords" value={s.seo?.keywords} onChange={(v) => setPath(["seo", "keywords"], v)} />
        </TabsContent>

        <TabsContent value="legal" className="mt-4 max-w-3xl bs-card p-6">
          <Tabs defaultValue="privacy">
            <TabsList className="flex flex-wrap h-auto">
              {["privacy", "terms", "cookie", "refund", "disclaimer"].map((k) => <TabsTrigger key={k} value={k}>{s.legal?.[k]?.title || k}</TabsTrigger>)}
            </TabsList>
            {["privacy", "terms", "cookie", "refund", "disclaimer"].map((k) => (
              <TabsContent key={k} value={k} className="mt-4 grid gap-4">
                <F label="Title" value={s.legal?.[k]?.title} onChange={(v) => setPath(["legal", k, "title"], v)} />
                <F label="Content" value={s.legal?.[k]?.content} onChange={(v) => setPath(["legal", k, "content"], v)} textarea rows={12} />
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>

        <TabsContent value="integrations" className="mt-4 max-w-2xl space-y-3">
          {[
            ["Email (Resend)", "Connected via Emergent managed integration", true],
            ["Payments (Stripe)", "Requires configuration — add keys to go live", false],
            ["Analytics", "Requires configuration — connect your analytics provider", false],
            ["Search Console", "Requires configuration", false],
          ].map(([name, desc, ok]) => (
            <div key={name} className="flex items-start justify-between gap-4 bs-card p-4">
              <div><p className="font-semibold">{name}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
              <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${ok ? "bg-success/15 text-success" : "bg-chart-4/15 text-chart-4"}`}>{ok ? "Connected" : "Requires config"}</span>
            </div>
          ))}
          <div className="flex items-start gap-3 rounded-xl border border-chart-4/40 bg-chart-4/5 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-chart-4" />
            <p className="text-sm text-muted-foreground">Secret values are never displayed. Keys are stored securely via environment configuration. Controls marked "Requires config" become active once the integration is connected.</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
